import moment from 'moment-timezone';
import { asTimeZone, fromUtcToStartDate, fromUtcToStartDateMoment } from '../utils/dateFormatter';
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByDoubleQueryWithList, getByQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import Customer from './Customer';
import StatusHistory from './StatusHistory';
import { collection, documentId, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { toE164PhoneNumber, toName, toPhoneNumberLocal } from '../utils/stringFormatter';
import { toLower } from 'lodash';
import ChatMessage from './ChatMessage';
import { sendBelugaImageMessage, sendBelugaMessage } from '../firebase/Beluga';
import { v4 as uuid } from 'uuid';

// mirrors the guard in functions/src/encounterManagement.js createFollowUpEncounter — the two
// sides must agree on when a follow-up already exists or they race to create duplicates.
const OPEN_FOLLOW_UP_STATUSES = ['SCHEDULED', 'REQUESTED', 'CREATED', 'DISQUALIFIED'];
const RECENTLY_STARTED_FOLLOW_UP_STATUSES = ['ASSIGNED', 'STARTED', 'DISQUALIFIED', 'DENIED'];
const FOLLOW_UP_COOLDOWN_DAYS = 10;

class Encounter {
    static collectionName = 'encounters';

    encounterType = null; // enum EncounterTypes
    visitType = 'CONSULT'; // CONSULT or FOLLOWUP

    refId = null; // visit id in foreign EHR

    pdfRef = null; // storage path for the written prescription pdf
    providerSignatureRef = null; // storage path for the prescriber's signature image
    userRefId = null; // user reference id in foreign EHR

    userId = null; // userId of the customer
    customer = new Customer(null).toDto(); // customer details snapshot

    providerId = null; // userId of the provider
    providerFirstName = null;
    providerLastName = null;
    providerNPI = null;

    practiceId = null; // the coveredEntityId of the practice this encounter is associated with

    status = "CREATED"; // enum EncounterStatuses CREATED, REQUESTED, SCHEDULED, ASSIGNED, STARTED, COMPLETED, CANCELLED, DISQUALIFIED, CLOSED, REPLACED
    statusHistory = []; // list of StatusHistory, historical changes to status

    notesCount = 0;
    notes = [];
    documentsCount = 0;
    documents = [];
    formsCount = 0;
    forms = [];

    // if a video visit, capture this info
    customerIPAddress = null;
    customerUserAgent = null;
    deviceInfo = null;

    photoRefId = null;
    videoRefId = null;

    scheduledAt = null;
    requestedAt = null;
    assignedAt = null;
    startedAt = null;
    completedAt = null;
    cancelledAt = null;
    closedAt = null;

    isScheduled = false;
    isAssigned = false;
    isStarted = false;
    isCompleted = false;
    isCancelled = false;
    isSynced = false;
    isClosed = false;

    orderIds = [];
    subscriptionIds = [];
    prescriptionIds = [];
    programId = null;

    prescriptionRequests = []; // array of PrescriptionRequest

    lastMessageAt = null;
    lastMessageBy = null;
    isRead = false;
    unreadCount = 0;
    messageName = null;
    messagesCount = 0;
    messages = []; // array of ChatMessage

    sentEmails = [];

    acceptTruthfulnessConsent = false;

    testMode = false;

    videoScheduleLink = null;
    videoVisitLink = null;
    videoScheduledDate = null;

    payoutId = null;
    isPaidFor = false;
    paidForAt = null;
    paidForAmount = 0;

    metadata = {}; // can store replacedNewRefId, replacedNewId, replacedOldRefId, replacedOldId, heldSubscriptionOverride (subscriptionStatus, overriddenBy, overriddenAt), etc.

    constructor(encounter) {
        if (isObjectNotEmpty(encounter)) {
            if (isStringNotEmpty(encounter.encounterType)) { this.encounterType = encounter.encounterType; }
            if (isStringNotEmpty(encounter.visitType)) { this.visitType = encounter.visitType; }

            if (isStringNotEmpty(encounter.refId)) { this.refId = encounter.refId; }
            if (isStringNotEmpty(encounter.pdfRef)) { this.pdfRef = encounter.pdfRef; }
            if (isStringNotEmpty(encounter.providerSignatureRef)) { this.providerSignatureRef = encounter.providerSignatureRef; }
            if (isStringNotEmpty(encounter.userRefId)) { this.userRefId = encounter.userRefId; }

            if (isStringNotEmpty(encounter.userId)) { this.userId = encounter.userId; }
            if (isObjectNotEmpty(encounter.customer)) { this.customer = new Customer(encounter.customer).toDto(); }

            if (isStringNotEmpty(encounter.providerId)) { this.providerId = encounter.providerId; }
            if (isStringNotEmpty(encounter.providerFirstName)) { this.providerFirstName = encounter.providerFirstName; }
            if (isStringNotEmpty(encounter.providerLastName)) { this.providerLastName = encounter.providerLastName; }
            if (isStringNotEmpty(encounter.providerNPI)) { this.providerNPI = encounter.providerNPI; }

            if (isStringNotEmpty(encounter.practiceId)) { this.practiceId = encounter.practiceId; }

            if (isStringNotEmpty(encounter.status)) { this.status = encounter.status; }
            if (isListNotEmpty(encounter.statusHistory)) { this.statusHistory = encounter.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status, []); }

            if (isNumberGreaterThanOrEqualToZero(encounter.notesCount)) { this.notesCount = encounter.notesCount; }
            if (isListNotEmpty(encounter.notes)) { this.notes = encounter.notes; }
            if (isNumberGreaterThanOrEqualToZero(encounter.documentsCount)) { this.documentsCount = encounter.documentsCount; }
            if (isListNotEmpty(encounter.documents)) { this.documents = encounter.documents; }
            if (isNumberGreaterThanOrEqualToZero(encounter.formsCount)) { this.formsCount = encounter.formsCount; }
            if (isListNotEmpty(encounter.forms)) { this.forms = encounter.forms; }

            if (isStringNotEmpty(encounter.customerIPAddress)) { this.customerIPAddress = encounter.customerIPAddress; }
            if (isStringNotEmpty(encounter.customerUserAgent)) { this.customerUserAgent = encounter.customerUserAgent; }
            if (isObjectNotEmpty(encounter.deviceInfo)) { this.deviceInfo = encounter.deviceInfo; }

            if (isStringNotEmpty(encounter.photoRefId)) { this.photoRefId = encounter.photoRefId; }
            if (isStringNotEmpty(encounter.videoRefId)) { this.videoRefId = encounter.videoRefId; }

            if (isNotNullOrUndefined(encounter.scheduledAt)) { this.scheduledAt = encounter.scheduledAt; }
            if (isNotNullOrUndefined(encounter.requestedAt)) { this.requestedAt = encounter.requestedAt; }
            if (isNotNullOrUndefined(encounter.assignedAt)) { this.assignedAt = encounter.assignedAt; }
            if (isNotNullOrUndefined(encounter.startedAt)) { this.startedAt = encounter.startedAt; }
            if (isNotNullOrUndefined(encounter.completedAt)) { this.completedAt = encounter.completedAt; }
            if (isNotNullOrUndefined(encounter.cancelledAt)) { this.cancelledAt = encounter.cancelledAt; }
            if (isNotNullOrUndefined(encounter.closedAt)) { this.closedAt = encounter.closedAt; }

            if (isBooleanTrue(encounter.isScheduled)) { this.isScheduled = true; }
            if (isBooleanTrue(encounter.isAssigned)) { this.isAssigned = true; }
            if (isBooleanTrue(encounter.isStarted)) { this.isStarted = true; }
            if (isBooleanTrue(encounter.isCompleted)) { this.isCompleted = true; }
            if (isBooleanTrue(encounter.isCancelled)) { this.isCancelled = true; }
            if (isBooleanTrue(encounter.isSynced)) { this.isSynced = true; }
            if (isBooleanTrue(encounter.isClosed)) { this.isClosed = true; }

            if (isListNotEmpty(encounter.orderIds)) { this.orderIds = encounter.orderIds; }
            if (isListNotEmpty(encounter.subscriptionIds)) { this.subscriptionIds = encounter.subscriptionIds; }
            if (isListNotEmpty(encounter.prescriptionIds)) { this.prescriptionIds = encounter.prescriptionIds; }
            if (isStringNotEmpty(encounter.programId)) { this.programId = encounter.programId; }

            if (isListNotEmpty(encounter.prescriptionRequests)) { this.prescriptionRequests = encounter.prescriptionRequests; }

            if (isNotNullOrUndefined(encounter.lastMessageAt)) { this.lastMessageAt = encounter.lastMessageAt; }
            if (isStringNotEmpty(encounter.lastMessageBy)) { this.lastMessageBy = encounter.lastMessageBy; }
            if (isBooleanTrue(encounter.isRead)) { this.isRead = true; }
            if (isNumberGreaterThanOrEqualToZero(encounter.unreadCount)) { this.unreadCount = encounter.unreadCount; }
            if (isStringNotEmpty(encounter.messageName)) { this.messageName = encounter.messageName; }
            if (isNumberGreaterThanOrEqualToZero(encounter.messagesCount)) { this.messagesCount = encounter.messagesCount; }
            if (isListNotEmpty(encounter.messages)) { this.messages = encounter.messages; }

            if (isListNotEmpty(encounter.sentEmails)) { this.sentEmails = encounter.sentEmails; }

            if (isBooleanTrue(encounter.acceptTruthfulnessConsent)) { this.acceptTruthfulnessConsent = true; }

            if (isBooleanTrue(encounter.testMode)) { this.testMode = true; }

            if (isStringNotEmpty(encounter.videoScheduleLink)) { this.videoScheduleLink = encounter.videoScheduleLink; }
            if (isStringNotEmpty(encounter.videoVisitLink)) { this.videoVisitLink = encounter.videoVisitLink; }
            if (isNotNullOrUndefined(encounter.videoScheduledDate)) { this.videoScheduledDate = encounter.videoScheduledDate; }

            if (isStringNotEmpty(encounter.payoutId)) { this.payoutId = encounter.payoutId; }
            if (isBooleanTrue(encounter.paidForAt)) { this.paidForAt = true; }
            if (isNotNullOrUndefined(encounter.paidForAt)) { this.paidForAt = encounter.paidForAt; }
            if (isNumberNotEmpty(encounter.paidForAmount)) { this.paidForAmount = encounter.paidForAmount; }

            // Metadata
            if (isObjectNotEmpty(encounter.metadata)) { this.metadata = encounter.metadata; }
        }
    }

    toDto = () => {
        return {
            encounterType: this.encounterType,
            visitType: this.visitType,

            refId: this.refId,
            pdfRef: this.pdfRef,
            providerSignatureRef: this.providerSignatureRef,
            userRefId: this.userRefId,

            userId: this.userId,
            customer: this.customer,

            providerId: this.providerId,
            providerFirstName: this.providerFirstName,
            providerLastName: this.providerLastName,
            providerNPI: this.providerNPI,

            practiceId: this.practiceId,

            status: this.status,
            statusHistory: this.statusHistory,

            notesCount: this.notesCount,
            notes: this.notes,
            documentsCount: this.documentsCount,
            documents: this.documents,
            formsCount: this.formsCount,
            forms: this.forms,

            customerIPAddress: this.customerIPAddress,
            customerUserAgent: this.customerUserAgent,
            deviceInfo: this.deviceInfo,

            photoRefId: this.photoRefId,
            videoRefId: this.videoRefId,

            scheduledAt: this.scheduledAt,
            requestedAt: this.requestedAt,
            assignedAt: this.assignedAt,
            startedAt: this.startedAt,
            completedAt: this.completedAt,
            cancelledAt: this.cancelledAt,
            closedAt: this.closedAt,

            isScheduled: this.isScheduled,
            isAssigned: this.isAssigned,
            isStarted: this.isStarted,
            isCompleted: this.isCompleted,
            isCancelled: this.isCancelled,
            isSynced: this.isSynced,
            isClosed: this.isClosed,

            orderIds: this.orderIds,
            subscriptionIds: this.subscriptionIds,
            prescriptionIds: this.prescriptionIds,
            programId: this.programId,

            prescriptionRequests: this.prescriptionRequests,

            lastMessageAt: this.lastMessageAt,
            lastMessageBy: this.lastMessageBy,
            isRead: this.isRead,
            unreadCount: this.unreadCount,
            messageName: this.messageName,
            messagesCount: this.messagesCount,
            messages: this.messages,

            sentEmails: this.sentEmails,

            acceptTruthfulnessConsent: this.acceptTruthfulnessConsent,

            testMode: this.testMode,

            videoScheduleLink: this.videoScheduleLink,
            videoVisitLink: this.videoVisitLink,
            videoScheduledDate: this.videoScheduledDate,

            payoutId: this.payoutId,
            isPaidFor: this.isPaidFor,
            paidForAt: this.paidForAt,
            paidForAmount: this.paidForAmount,

            metadata: this.metadata
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Encounter.collectionName, this.toDto());
        }
    }

    // keyed on prescriptionIds rather than status alone: a prescriber can write the script before the send
    // finishes marking the visit STARTED, and that visit is done even though its status never moves on
    static isAwaitingPrescription = (encounter) => {
        return encounter?.status === 'STARTED' && isListEmpty(encounter?.prescriptionIds);
    }

    // routing lookup for the reactivation flow: the check-in the patient can be sent into.
    // deliberately SCHEDULED-only — findExistingBlockingFollowUpEncounter is the wider dedup guard.
    static getScheduledFollowUpForSubscription = async (subscription) => {
        if (isObjectEmpty(subscription) || isStringEmpty(subscription.userId) || isStringEmpty(subscription.id)) return null;
        const scheduledEncounters = await getByDoubleQuery(Encounter.collectionName, 'status', 'SCHEDULED', '==', 'subscriptionIds', subscription.id, 'array-contains');
        return (scheduledEncounters || []).find((encounter) => encounter?.visitType === 'FOLLOWUP') ?? null;
    }

    /**
     * Identify if a follow-up encounter already exists or is in progress for the subscription, so we can avoid creating a duplicate follow-up erroneously.
     * TODO: add a similar comment to the Backend scheduled (cron) function to clarify this same intent for that function.
     */
    static findExistingBlockingFollowUpEncounter = (encounters, nextEncounterDate) => {
        const followUpEncounters = (encounters || []).filter((encounter) => encounter?.visitType === 'FOLLOWUP');
        if (isListEmpty(followUpEncounters)) return null;

        const openFollowUp = followUpEncounters.find((encounter) => OPEN_FOLLOW_UP_STATUSES.includes(encounter?.status));
        if (isObjectNotEmpty(openFollowUp)) return openFollowUp;

        // a visit still with the prescriber blocks however long it takes: nextEncounterDate is the pre-visit one until RX_WRITTEN sets it
        const awaitingPrescriptionFollowUp = followUpEncounters.find(Encounter.isAwaitingPrescription);
        if (isObjectNotEmpty(awaitingPrescriptionFollowUp)) return awaitingPrescriptionFollowUp;

        // a check-in started within the cooldown window means it is too soon for another
        const recentlyStartedFollowUp = followUpEncounters.find((encounter) => {
            if (!RECENTLY_STARTED_FOLLOW_UP_STATUSES.includes(encounter?.status) || !isNotNullOrUndefined(encounter?.startedAt)) return false;
            const cooldownEndsAt = asTimeZone(encounter.startedAt, 'US/Pacific')?.add(FOLLOW_UP_COOLDOWN_DAYS, 'days');
            return isNotNullOrUndefined(cooldownEndsAt) && cooldownEndsAt >= moment();
        });
        if (isObjectNotEmpty(recentlyStartedFollowUp)) return recentlyStartedFollowUp;

        if (!isNotNullOrUndefined(nextEncounterDate)) return null;
        const scheduledDate = fromUtcToStartDateMoment(nextEncounterDate, 'US/Pacific');
        if (!isNotNullOrUndefined(scheduledDate)) return null;
        return followUpEncounters.find((encounter) => {
            const encounterDate = isNotNullOrUndefined(encounter?.scheduledAt) ? fromUtcToStartDateMoment(encounter.scheduledAt, 'US/Pacific') : null;
            return isNotNullOrUndefined(encounterDate) && encounterDate.isSame(scheduledDate, 'day');
        }) ?? null;
    }

    // Admin surfaces (GenerateNextEncounterModal, SubscriptionUnpauseModal) rely on this rule:
    // block only when a SCHEDULED encounter already exists on the subscription. The patient
    // dashboard needs the wider guard instead — see createFollowUpEncounterUnlessBlocked.
    static createFollowUpEncounter = async (subscription, programs) => {
        if (isObjectNotEmpty(subscription) && isStringNotEmpty(subscription.id) && isStringNotEmpty(subscription.userId) && isObjectNotEmpty(subscription.customer) && isNotNullOrUndefined(subscription.nextEncounterDate)) {
            const scheduledEncounters = await getByDoubleQuery('encounters', 'status', 'SCHEDULED', '==', 'subscriptionIds', subscription.id, 'array-contains');
            if (isListEmpty(scheduledEncounters)) {
                let newEncounter = await new Encounter({
                    encounterType: subscription?.encounterType,
                    visitType: 'FOLLOWUP',

                    userId: subscription.userId,
                    customer: new Customer({ ...subscription.customer }).toDto(),

                    practiceId: subscription?.coveredEntityId,

                    status: 'SCHEDULED',
                    isScheduled: true,
                    scheduledAt: fromUtcToStartDate(subscription.nextEncounterDate, 'US/Pacific'),

                    orderIds: [],
                    subscriptionIds: [subscription.id],
                    programId: subscription.programId,

                    messageName: (programs.find(i => i.id === subscription?.programId)?.name ?? '') + ' Follow-Up',

                    prescriptionRequests: [],

                    testMode: isBooleanTrue(subscription.testMode),

                    refId: uuid()
                }).create();

                if (isObjectNotEmpty(newEncounter) && isStringNotEmpty(newEncounter.id)) {
                    return newEncounter;
                }
            }
        }

        return null;
    }

    // TODO: migrate this to a Backend function so the dashboard and the scheduled job cannot drift.
    static createFollowUpEncounterUnlessBlocked = async (subscription, programs) => {
        if (isObjectNotEmpty(subscription) && isStringNotEmpty(subscription.id) && isStringNotEmpty(subscription.userId) && isObjectNotEmpty(subscription.customer) && isNotNullOrUndefined(subscription.nextEncounterDate)) {
            const subscriptionEncounters = await Encounter.getBySubscriptionId(subscription.id);
            const existingBlockingFollowUpEncounter = Encounter.findExistingBlockingFollowUpEncounter(subscriptionEncounters, subscription.nextEncounterDate);
            if (isObjectEmpty(existingBlockingFollowUpEncounter)) {
                let newEncounter = await new Encounter({
                    encounterType: subscription?.encounterType,
                    visitType: 'FOLLOWUP',

                    userId: subscription.userId,
                    customer: new Customer({ ...subscription.customer }).toDto(),

                    practiceId: subscription?.coveredEntityId,

                    status: 'SCHEDULED',
                    isScheduled: true,
                    scheduledAt: fromUtcToStartDate(subscription.nextEncounterDate, 'US/Pacific'),

                    orderIds: [],
                    subscriptionIds: [subscription.id],
                    programId: subscription.programId,

                    messageName: (programs.find(i => i.id === subscription?.programId)?.name ?? '') + ' Follow-Up',

                    prescriptionRequests: [],

                    testMode: isBooleanTrue(subscription.testMode),

                    refId: uuid()
                }).create();

                if (isObjectNotEmpty(newEncounter) && isStringNotEmpty(newEncounter.id)) {
                    return newEncounter;
                }
            }
        }

        return null;
    }

    static sendMessage = async (encounterId, message = null, mediaRefId = null, profile, testMode = false, clientId = null) => {
        const encounter = await Encounter.getById(encounterId);
        if (isObjectNotEmpty(profile) && isStringNotEmpty(profile?.userId) && isStringNotEmpty(encounterId) && isObjectNotEmpty(encounter) && isStringNotEmpty(encounter?.refId)) {
            let newMessage = new ChatMessage({
                // the caller's id when it has one, so the message shown optimistically and the one
                // stored are the same message rather than two bubbles saying the same thing
                id: clientId,
                encounterId: encounterId,
                message: isStringNotEmpty(message) ? `${message.replace(/(?:\r|\n)/g, '<br><br>').replace(/(?:\r\n)/g, '<br><br>')}` : null,
                mediaRefId: mediaRefId,

                sentName: toName(profile),
                sentBy: profile?.userId,
                sentAt: moment().utc().toDate()
            }).toDto();

            let existingMessages = isListNotEmpty(encounter.messages) ? [...encounter.messages] : [];

            // a resend carries the id of the attempt it is repeating, so a first attempt that
            // actually stored the message before failing back to the client is answered from what
            // is already there rather than posting to the pharmacy and appending a second copy
            const storedMessage = existingMessages.find((item) => item?.id === newMessage.id);
            if (isObjectNotEmpty(storedMessage)) {
                return storedMessage;
            }

            let updatedMessages = [...existingMessages, newMessage];
            let updatedEncounterFields = {
                lastMessageAt: newMessage.sentAt,
                lastMessageBy: newMessage.sentBy,
                isRead: false,
                unreadCount: encounter.unreadCount + 1,
                messages: updatedMessages,
                messagesCount: updatedMessages.length
            };

            // console.log('mediaRefId', mediaRefId);
            // console.log('message', message);

            // send message(s) to Beluga
            if (isStringNotEmpty(message) && isStringNotEmpty(mediaRefId)) {
                const { status: sendMessageStatus, info } = await sendBelugaMessage(profile?.firstName, profile?.lastName, message, encounter?.refId, testMode);
                // console.log('sendMessageStatus', sendMessageStatus);

                const { status: sendMediaStatus, info: mediaInfo } = await sendBelugaImageMessage(profile?.firstName, profile?.lastName, mediaRefId, encounter?.refId, testMode);
                // console.log('sendMediaStatus', sendMediaStatus);

                if (sendMessageStatus === 200 && sendMediaStatus === 200) {
                    let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                    // console.log('new message was added to the database', success);
                    if (isBooleanTrue(success)) {
                        return newMessage;
                    }
                } else if ((sendMessageStatus === 400 && info === 'Visit does not exist') || (sendMediaStatus === 400 && mediaInfo === 'Visit does not exist')) {
                    const { status: sendMessageStatus2 } = await sendBelugaMessage(profile?.firstName, profile?.lastName, message, encounter?.id, testMode);
                    // console.log('sendMessageStatus', sendMessageStatus);

                    const { status: sendMediaStatus2 } = await sendBelugaImageMessage(profile?.firstName, profile?.lastName, mediaRefId, encounter?.id, testMode);
                    // console.log('sendMediaStatus', sendMediaStatus);

                    if (sendMessageStatus2 === 200 && sendMediaStatus2 === 200) {
                        let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                        // console.log('new message was added to the database', success);
                        if (isBooleanTrue(success)) {
                            return newMessage;
                        }
                    }
                }
            } else if (isStringNotEmpty(message)) {
                const { status: sendMessageStatus, info } = await sendBelugaMessage(profile?.firstName, profile?.lastName, newMessage.message, encounter?.refId, testMode);
                // console.log('sendMessageStatus', sendMessageStatus);

                if (sendMessageStatus === 200) {
                    let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                    // console.log('new message was added to the database', success);
                    if (isBooleanTrue(success)) {
                        return newMessage;
                    }
                } else if (sendMessageStatus === 400 && info === 'Visit does not exist') {
                    const { status: sendMessageStatus2 } = await sendBelugaMessage(profile?.firstName, profile?.lastName, message, encounter?.id, testMode);
                    // console.log('sendMessageStatus', sendMessageStatus);

                    if (sendMessageStatus2 === 200) {
                        let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                        // console.log('new message was added to the database', success);
                        if (isBooleanTrue(success)) {
                            return newMessage;
                        }
                    }
                }
            } else if (isStringNotEmpty(mediaRefId)) {
                // console.log('mediaRefId', mediaRefId);
                const { status: sendMediaStatus, info: mediaInfo } = await sendBelugaImageMessage(profile?.firstName, profile?.lastName, mediaRefId, encounter?.refId, testMode);
                // console.log('sendMediaStatus', sendMediaStatus);

                if (sendMediaStatus === 200) {
                    let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                    // console.log('new message was added to the database', success);
                    if (isBooleanTrue(success)) {
                        return newMessage;
                    }
                } else if (sendMediaStatus === 400 && mediaInfo === 'Visit does not exist') {
                    const { status: sendMediaStatus2 } = await sendBelugaImageMessage(profile?.firstName, profile?.lastName, mediaRefId, encounter?.id, testMode);
                    // console.log('sendMediaStatus', sendMediaStatus);

                    if (sendMediaStatus2 === 200) {
                        let success = await Encounter.updateFields(encounterId, updatedEncounterFields);
                        // console.log('new message was added to the database', success);
                        if (isBooleanTrue(success)) {
                            return newMessage;
                        }
                    }
                }
            }
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Encounter.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Encounter.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedEncounter) => {
        const updatedObj = new Encounter(updatedEncounter);
        if (updatedObj.isValid() && id === updatedEncounter.id) {
            return await update(Encounter.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated encounter must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Encounter.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Encounter.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getByRefId = async (refId) => {
        if (isStringNotEmpty(refId)) {
            let matches = await getBySingleQuery(Encounter.collectionName, 'refId', refId, '==');
            if (isListNotEmpty(matches)) {
                return matches[0];
            } else {
                return null;
            }
        } else {
            throw new Error("refId is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Encounter.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Encounter.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Encounter.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        if (isListNotEmpty(status)) {
            return await getByDoubleQueryWithList(Encounter.collectionName, 'userId', userId, '==', 'status', status, 'in');
        }

        return await getByDoubleQuery(Encounter.collectionName, 'userId', userId, '==', 'status', status, '==');
    }

    static getBySubscriptionId = async (subscriptionId) => {
        return await getBySingleQuery(Encounter.collectionName, 'subscriptionIds', subscriptionId, 'array-contains');
    }

    static getBySubscriptionIds = async (subscriptionIds) => {
        return await getBySingleQuery(Encounter.collectionName, 'subscriptionIds', subscriptionIds, 'array-contains-any');
    }

    static getBySubscriptionIdAndUserId = async (subscriptionId, userId) => {
        return await getByDoubleQuery(Encounter.collectionName, 'userId', userId, '==', 'subscriptionIds', subscriptionId, 'array-contains');
    }

    static getBySubscriptionIdAndStatus = async (subscriptionId, status = []) => {
        return await getByDoubleQuery(Encounter.collectionName, 'status', status, 'in', 'subscriptionIds', subscriptionId, 'array-contains');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Encounter.collectionName, ids, 'in');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, Encounter.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        // console.log(snapShot)
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id, // append document id to each document
                ...doc.data()
            };
        });
    }

    static getMissedForUsers = async (userIds = []) => {
        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        return new Promise((res) => {
            // don't run if there aren't any ids
            if (!userIds || !userIds.length) return res([]);

            let batches = [];

            while (userIds.length) {
                // firestore limits batches to 10
                const batch = userIds.splice(0, 10);

                // add the batch request to to a queue
                batches.push(
                    new Promise(response => { getByQuery(query(collection(db, Encounter.collectionName), where('status', 'in', ['SCHEDULED', 'REQUESTED']), where('visitType', '==', 'FOLLOWUP'), where('scheduledAt', '<=', startDate), where('userId', 'in', [...batch]), orderBy('scheduledAt', 'asc'), orderBy('__name__', 'asc'))).then(results => response(results)) })
                );
            }

            // after all of the data is fetched, return it
            Promise.all(batches).then(content => {
                res(content.flat());
            });
        });
    }

    static getMissed = async (userId = null) => {
        let queryConstraints = [];

        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        if (isStringNotEmpty(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        queryConstraints.push(where('status', 'in', ['SCHEDULED', 'REQUESTED']));
        queryConstraints.push(where('visitType', '==', 'FOLLOWUP'));
        queryConstraints.push(where('scheduledAt', '<=', startDate));

        queryConstraints.push(orderBy('scheduledAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getMissedCount = async (userId = null) => {
        let queryConstraints = [];

        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        if (isStringNotEmpty(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        queryConstraints.push(where('status', 'in', ['SCHEDULED', 'REQUESTED']));
        queryConstraints.push(where('visitType', '==', 'FOLLOWUP'));
        queryConstraints.push(where('scheduledAt', '<=', startDate));

        queryConstraints.push(orderBy('scheduledAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        let q = query(collection(db, Encounter.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static search = async (searchCriteria) => {
        let { userId, providerId, practiceId, firstName, lastName, email, phone, encounterId, encounterRefId } = searchCriteria;

        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }
        if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }
        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName)));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName)));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where(documentId(), '==', encounterId));
        }
        if (isStringNotEmptyOrZeroLength(encounterRefId)) {
            queryConstraints.push(where('refId', '==', encounterRefId));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        const { acceptTruthfulnessConsent, testMode, userId, status, providerId, practiceId, orderId, subscriptionId, encounterType, visitType, programId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(acceptTruthfulnessConsent)) {
            queryConstraints.push(where('acceptTruthfulnessConsent', '==', true));
        } else if (isBooleanFalse(acceptTruthfulnessConsent)) {
            queryConstraints.push(where('acceptTruthfulnessConsent', '==', false));
        }

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(providerId)) {
            queryConstraints.push(where('providerId', 'in', providerId));
        } else if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }

        if (isListNotEmpty(practiceId)) {
            queryConstraints.push(where('practiceId', 'in', practiceId));
        } else if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }

        if (isListNotEmpty(orderId)) {
            queryConstraints.push(where('orderId', 'array-contains-any', orderId));
        } else if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderId', 'array-contains', orderId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains-any', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains', subscriptionId));
        }

        if (isListNotEmpty(encounterType)) {
            queryConstraints.push(where('encounterType', 'in', encounterType));
        } else if (isStringNotEmptyOrZeroLength(encounterType)) {
            queryConstraints.push(where('encounterType', '==', encounterType));
        }

        if (isListNotEmpty(visitType)) {
            queryConstraints.push(where('visitType', 'in', visitType));
        } else if (isStringNotEmptyOrZeroLength(visitType)) {
            queryConstraints.push(where('visitType', '==', visitType));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Encounter.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { acceptTruthfulnessConsent, testMode, userId, status, providerId, practiceId, orderId, subscriptionId, encounterType, visitType, programId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(acceptTruthfulnessConsent)) {
            queryConstraints.push(where('acceptTruthfulnessConsent', '==', true));
        } else if (isBooleanFalse(acceptTruthfulnessConsent)) {
            queryConstraints.push(where('acceptTruthfulnessConsent', '==', false));
        }

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(providerId)) {
            queryConstraints.push(where('providerId', 'in', providerId));
        } else if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }

        if (isListNotEmpty(practiceId)) {
            queryConstraints.push(where('practiceId', 'in', practiceId));
        } else if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }

        if (isListNotEmpty(orderId)) {
            queryConstraints.push(where('orderId', 'array-contains-any', orderId));
        } else if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderId', 'array-contains', orderId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains-any', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionIds', 'array-contains', subscriptionId));
        }

        if (isListNotEmpty(encounterType)) {
            queryConstraints.push(where('encounterType', 'in', encounterType));
        } else if (isStringNotEmptyOrZeroLength(encounterType)) {
            queryConstraints.push(where('encounterType', '==', encounterType));
        }

        if (isListNotEmpty(visitType)) {
            queryConstraints.push(where('visitType', 'in', visitType));
        } else if (isStringNotEmptyOrZeroLength(visitType)) {
            queryConstraints.push(where('visitType', '==', visitType));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Encounter.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Encounter.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Encounter.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Encounter.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Encounter.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Encounter;